/**
 * @file
 * Display device properties. These are all reactive properties.
 *
 * @example
 * $notify($media.pixelRatio);
 * @example
 * $notify($media.theme);
 * @example
 * $notify($media.orientation);
 * @example
 * $notify($media.fontSize);
 */


const haveAddEventListener = !!window.MediaQueryList?.prototype.addEventListener;


const themes = $build.platform == 'web'
    ? ['light','dark','hotdog','hsbc','commsec']
    : ['light','dark'];

const breakpoints = {
    s: 320,
    m: 568,
    l: 769,
    xl: 992,
    xxl: 1200,
    xxxl: 1600
};


const $media = Vue.observable(/** @alias $media */ {
    /** @member {number} - The ratio of physical pixels to CSS pixels for the current display device. */
    pixelRatio: void 0,
    /** @member {string} - Color scheme; `dark` or `light`.  This may differ from [`$app.option.theme`](#/global/app), which is the requested theme. */
    theme: void 0,
    /** @member {string} - `portrait` if the device is in portrait orientation (height greater than width); otherwise `landscape`. */
    orientation: void 0,
    /** @member {number} - Root font size in pixels. This may differ from [`$app.option.fontSize`](#/global/app), which is the requested size. */
    fontSize: void 0,

    /**
     * @member {boolean} s - `true` if the window is at least 320px wide.
     * @memberof $media
     */
    /**
     * @member {boolean} m - `true` if the window is at least 568px wide.
     * @memberof $media
     */
    /**
     * @member {boolean} l - `true` if the window is at least 769px wide.
     * @memberof $media
     */
    /**
     * @member {boolean} xl - `true` if the window is at least 992px wide.
     * @memberof $media
     */
    /**
     * @member {boolean} xxl - `true` if the window is at least 1200px wide.
     * @memberof $media
     */
    /**
     * @member {boolean} xxxl - `true` if the window is at least 1600px wide.
     * @memberof $media
     */
    ...breakpoints
});


Object.keys(breakpoints).forEach(name => {
    const match = matchMedia('screen and (min-width:' + breakpoints[name] + 'px)');
    const fn = () => {
        $media[name] = match.matches;
    };
    fn();
    if (haveAddEventListener) {
        match.addEventListener('change', fn);
    } else {
        match.addListener(fn);
    }
});


const portrait = matchMedia('(orientation:portrait)');
const portraitfn = () => {
    /** @ignore */
    $media.orientation = portrait.matches ? 'portrait' : 'landscape';
};
portraitfn();

if (haveAddEventListener) {
    portrait.addEventListener('change', portraitfn);
} else {
    portrait.addListener(portraitfn);
}


const pixelRatioFn = () => {
    const pixelRatio = window.devicePixelRatio || 1;
    $media.pixelRatio = pixelRatio;
    if (haveAddEventListener) {
        matchMedia('(resolution:' + pixelRatio + 'dppx)')
            .addEventListener('change', pixelRatioFn, { once: true });
    }
}
pixelRatioFn();


/**
 * @private
 * Retrieve the font size of the HTML root element.
 */
function getFontSize() {
    return parseFloat(getComputedStyle(document.documentElement).fontSize);
}


/**
 * @private
 * Set the font size of the HTML root element.
 */
function setFontSize(fontSize) {
    if (fontSize !== getFontSize()) {
        document.documentElement.style.fontSize = fontSize + 'px';
    }
    /** @ignore */
    $media.fontSize = getFontSize();
}


function setThemeImpl(theme) {
    if (!themes.includes(theme)) {
        theme = themes[0];
    }
    // for older web apps hosted by iserver
    if (window.__theme) {
        window.__theme(/** @ignore */ $media.theme = theme);
        return;
    }
    // clone any <link> elements that are theme dependent, changing their theme
    const link = document.querySelectorAll('link[data-theme]');
    for (let i = 0; i < link.length; i++) {
        const csso = link[i];
        const href = csso.href.replace(/\.(dark|light|hotdog)\./, '.' + theme + '.');
        if (href != csso.href) {
            const cssn = csso.cloneNode();
            cssn.href = href;
            cssn.onload = () => {
                /** @ignore */
                $media.theme = theme;
                csso.parentElement.removeChild(csso);
            };
            cssn.onerror = () => {
                cssn.parentElement.removeChild(cssn);
            };
            csso.parentElement.insertBefore(cssn, csso.nextElementSibling);
        } else {
            /** @ignore */
            $media.theme = theme;
        }
    }
}


/**
 * @private
 * Set the color scheme.
 */
function setTheme(theme) {
    let dark;
    function darkfn() {
        const theme = dark.matches ? 'dark' : 'light';
        if (theme != $media.theme) {
            setThemeImpl(theme);
        }
    }
    if (theme == 'auto') {
        dark = matchMedia('(prefers-color-scheme:dark)');
        if (haveAddEventListener) {
            dark.addEventListener('change', darkfn);
        } else {
            dark.addListener(darkfn);
        }
        darkfn();
    } else {
        if (dark) {
            if (haveAddEventListener) {
                dark.removeEventListener('change', darkfn);
            } else {
                dark.removeListener(darkfn);
            }
            dark = void 0;
        }
        setThemeImpl(theme);
    }
}


/**
 * @private
 * Sets "invert rise/fall colors" mode.
 */
function setTraditionalChineseColors(traditionalChineseColors) {
    if (document.body) {
        document.body.classList[traditionalChineseColors ? 'add' : 'remove']('_trcc');
    } else {
        addEventListener('DOMContentLoaded', () => {
            setTraditionalChineseColors(traditionalChineseColors);
        });
    }
}


export {
    setFontSize,
    setTheme,
    setTraditionalChineseColors
};
export default $media;
